import { useTranslation } from '@kgc/i18n';
import { ClipboardList, FileText } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChangelogTab } from './components/ChangelogTab';
import { ReleaseTestingTab } from './components/ReleaseTestingTab';
import { SearchFilterBar } from './components/SearchFilterBar';
import { useReleaseFilters } from './hooks/useReleaseFilters';
import { useTestResults } from './hooks/useTestResults';

type TabId = 'changelog' | 'testing';

export function ReleaseHubPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterHook = useReleaseFilters();
  const { data: allResults } = useTestResults();

  const initialTab = (searchParams.get('tab') as TabId) || 'changelog';
  const initialVersion = searchParams.get('v');

  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const [scrollTarget, setScrollTarget] = useState<string | null>(initialVersion);

  const switchTab = useCallback(
    (tab: TabId, version?: string) => {
      setActiveTab(tab);
      const params: Record<string, string> = { tab };
      if (version) params['v'] = version.replace(/\./g, '-');
      setSearchParams(params, { replace: true });
      setScrollTarget(version ?? null);
    },
    [setSearchParams]
  );

  const handleNavigateToTesting = useCallback(
    (version: string) => switchTab('testing', version),
    [switchTab]
  );

  const handleNavigateToChangelog = useCallback(
    (version: string) => switchTab('changelog', version),
    [switchTab]
  );

  // Status counts for SearchFilterBar badges
  const statusCounts = (() => {
    if (!allResults) return { passed: 0, failed: 0, not_tested: 0 };
    const tested = new Set(allResults.map(r => `${r.version}-${r.featureIndex}`));
    const totalFeatures = filterHook.filteredTests.reduce((a, e) => a + e.features.length, 0);
    const passed = allResults.filter(r => r.status === 'passed').length;
    const failed = allResults.filter(r => r.status === 'failed').length;
    return { passed, failed, not_tested: Math.max(0, totalFeatures - tested.size) };
  })();

  const tabs: { id: TabId; label: string; icon: typeof FileText }[] = [
    { id: 'changelog', label: t('releaseHub.tabs.changelog'), icon: FileText },
    {
      id: 'testing',
      label: t('releaseHub.tabs.testing'),
      icon: ClipboardList,
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 md:p-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold">{t('releaseHub.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('releaseHub.subtitle')}</p>
      </div>

      {/* Tab navigation */}
      <div className="flex rounded-t-lg overflow-hidden border border-border">
        {tabs.map(({ id, label, icon: Icon }) => {
          const isActive = activeTab === id;
          const activeStyles =
            id === 'changelog'
              ? 'bg-emerald-900/40 text-emerald-300 border-b-3 border-emerald-400'
              : 'bg-amber-900/30 text-amber-300 border-b-3 border-amber-400';
          return (
            <button
              key={id}
              onClick={() => switchTab(id)}
              className={`flex flex-1 items-center justify-center gap-2 px-4 py-3.5 text-sm font-semibold transition-all ${
                isActive
                  ? activeStyles
                  : 'bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted/50 border-b-3 border-transparent'
              }`}
            >
              <Icon className={`h-4 w-4 ${isActive ? '' : 'opacity-50'}`} />
              {label}
            </button>
          );
        })}
      </div>

      {/* Search & Filter Bar */}
      <SearchFilterBar
        activeTab={activeTab}
        filterHook={filterHook}
        statusCounts={activeTab === 'testing' ? statusCounts : undefined}
      />

      {/* Tab content */}
      {activeTab === 'changelog' && (
        <ChangelogTab
          filteredData={filterHook.filteredChangelog}
          onNavigateToTesting={handleNavigateToTesting}
          scrollToVersion={scrollTarget}
          noResults={filterHook.hasActiveFilters && filterHook.filteredChangelog.length === 0}
          onClearFilters={filterHook.clearFilters}
        />
      )}
      {activeTab === 'testing' && (
        <ReleaseTestingTab
          filteredData={
            filterHook.filters.statuses.length > 0 && allResults
              ? filterHook.filteredTestsWithStatus(allResults)
              : filterHook.filteredTests
          }
          onNavigateToChangelog={handleNavigateToChangelog}
          scrollToVersion={scrollTarget}
          noResults={filterHook.hasActiveFilters && filterHook.filteredTests.length === 0}
          onClearFilters={filterHook.clearFilters}
        />
      )}
    </div>
  );
}
